/* import __COLOCATED_TEMPLATE__ from './channel-selector.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';

export type PreviewChannel = 'email' | 'chat' | 'voice';

interface ChannelOption {
  label: string;
  value: PreviewChannel;
}

export interface ChannelSelectorArgs {
  onClick?: (channel: string) => void;
  currentChannel: PreviewChannel;
  showAllChannels?: boolean;
  showEmail?: boolean;
}

export default class ChannelSelector extends Component<ChannelSelectorArgs> {
  @service declare intl: IntlService;
  get icon() {
    switch (this.args.currentChannel) {
      case 'email':
        return 'message';
      case 'voice':
        return 'phone';
      default:
        return 'chat-bubble';
    }
  }

  get tooltipKey() {
    return this.args.currentChannel === 'email'
      ? 'operator.custom-bot.editor.preview.controls.channel-selector.preview-chat'
      : 'operator.custom-bot.editor.preview.controls.channel-selector.preview-email';
  }

  get selectedLabel(): string {
    let option = this.channelOptions.find((o) => o.value === this.args.currentChannel);
    return (
      option?.label ??
      this.intl.t('operator.custom-bot.editor.preview.controls.channel-selector.chat')
    );
  }

  get showDropdown() {
    return this.args.showAllChannels ?? false;
  }

  get channelOptions(): ChannelOption[] {
    let options: ChannelOption[] = [
      {
        label: this.intl.t('operator.custom-bot.editor.preview.controls.channel-selector.chat'),
        value: 'chat',
      },
    ];

    if (this.args.showEmail !== false) {
      options.push({
        label: this.intl.t('operator.custom-bot.editor.preview.controls.channel-selector.email'),
        value: 'email',
      });
    }

    options.push({
      label: this.intl.t('operator.custom-bot.editor.preview.controls.channel-selector.voice'),
      value: 'voice',
    });

    return options;
  }

  @action
  selectChannel(channel: PreviewChannel, hideDropdown: () => void) {
    this.args.onClick?.(channel);
    hideDropdown();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::Preview::Controls::ChannelSelector': typeof ChannelSelector;
    'workflows/preview/controls/channel-selector': typeof ChannelSelector;
  }
}
